import { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useUserAuth } from "../context/UserAuthContext";

const Services = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const { user, language, token } = useUserAuth();
  const merchant_id = user.uid;
  const [data, setData] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendors, setVendors] = useState([]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${base_url}/api/v1/manager/vendors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVendors(res.data.vendors);
    } catch (err) {
      console.log("Error fetching vendors:", err);
    }
  };

  const getEnquireList = async () => {
    const response = await axios.get(
      `${base_url}/api/v1/manager/products`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          vendor_id: selectedVendor || undefined,
        },
      },
    );
    setData(response.data.products);
  };

  useEffect(() => {
    if (!merchant_id) return;
    getEnquireList();
    fetchVendors();
  }, [token, selectedVendor]);

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card shadow-lg border-0 rounded-3">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  <h2 className="mb-0 fw-bold" style={{ color: '#2c3e50' }}>
                    {language === "en" ? "✨ Services" : "✨ सेवाएं"}
                  </h2>
                  <div className="d-flex align-items-center gap-2">
                    <label className="fw-semibold text-muted">
                      {language === "en" ? "Filter by Vendor:" : "विक्रेता द्वारा फ़िल्टर करें:"}
                    </label>
                    <select
                      className="form-select rounded-pill px-3 py-2"
                      value={selectedVendor || ""}
                      onChange={(e) => setSelectedVendor(e.target.value || null)}
                      style={{ 
                        width: "220px", 
                        borderColor: '#dee2e6',
                        backgroundColor: '#f8f9fa',
                        fontWeight: '500'
                      }}
                    >
                      <option value="">📋 {language === "en" ? "All Vendors" : "सभी विक्रेता"}</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.business_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover table-bordered shadow-sm rounded-3 overflow-hidden">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr className="text-center">
                        <th style={{ width: '70px', color: '#495057', fontWeight: '600' }}>#</th>
                        <th style={{ color: '#495057', fontWeight: '600' }}>Vendor Name</th>
                        <th style={{ color: '#495057', fontWeight: '600' }}>Name</th>
                        <th style={{ width: '120px', color: '#495057', fontWeight: '600' }}>Price</th>
                        <th style={{ width: '100px', color: '#495057', fontWeight: '600' }}>Status</th>
                        <th style={{ width: '160px', color: '#495057', fontWeight: '600' }}>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.length > 0 ? (
                        data.map((item, index) => (
                          <tr key={index} className="align-middle" style={{ transition: 'all 0.3s ease' }}>
                            <td className="text-center fw-semibold">{index + 1}</td>
                            <td>
                              <span className="fw-medium">{item.vendor_name}</span>
                            </td>
                            <td className="fw-medium">{item.name}</td>
                            <td className="text-center">
                              <span className="fw-bold text-success">
                                ₹{parseFloat(item.price).toLocaleString()}
                              </span>
                            </td>
                            <td className="text-center">
                              <span
                                className={`badge px-3 py-2 rounded-pill fs-7 ${
                                  item.stock ? "bg-success" : "bg-warning"
                                }`}
                                style={{ fontSize: '0.75rem' }}
                              >
                                {item.stock ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="text-center text-muted">
                              <small>{dayjs(item.created_at).format("DD MMMM, YYYY")}</small>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <div className="text-muted">
                              <span style={{ fontSize: '3rem' }}>📭</span>
                              <p className="mt-2 mb-0">
                                {language === "en" 
                                  ? "No services found" 
                                  : "कोई सेवा नहीं मिली"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {data.length > 0 && (
                  <div className="mt-3 text-muted text-end">
                    <small>
                      {language === "en" 
                        ? `Showing ${data.length} service${data.length > 1 ? 's' : ''}` 
                        : `${data.length} सेवा${data.length > 1 ? 'एं' : ''} दिखा रहे हैं`}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .table-hover tbody tr:hover {
          background-color: #f8f9fa;
          transform: scale(1.01);
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .table {
          margin-bottom: 0;
        }
        
        .form-select:focus {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        .badge {
          font-weight: 500;
          letter-spacing: 0.3px;
        }
        
        @media (max-width: 768px) {
          .d-flex {
            flex-direction: column;
            align-items: flex-start !important;
          }
          
          .table th, .table td {
            font-size: 0.875rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Services;